import { AfterViewInit, Component, Inject, LOCALE_ID, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { InvisibleReCaptchaComponent } from 'ngx-captcha';
import { ApiService, getResetPwTokenAPI, resetPasswordAPI } from 'src/app/api/api.service';
import { homeRoute, passwordRoute } from 'src/app/route-names';
import { ApiAboutService } from 'src/app/Services/api-about.service';
import { DialogService, DialogType } from 'src/app/Services/dialog.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrl: './reset-password.component.scss',
    standalone: false
})
export class ResetPasswordComponent implements AfterViewInit {
  @ViewChild('captchaRef') captchaComponent!: InvisibleReCaptchaComponent;
  @ViewChild(MatStepper) stepper: MatStepper | undefined;
  isLinear = true;

  getTokenForm: FormGroup;
  numbers = [0, 1, 2, 3, 4, 5];
  codeForm: FormGroup;

  recaptchaResponse = '';
  readonly recaptchaSiteKey: string = environment.recaptchaSiteKey;
  
  voterID: string | undefined;

  codeSent: boolean = false;
  codeVerified: boolean = false;

  displaySpinner: boolean = false;

  numResetAttempts: number = 0;
  readonly MAX_RESET_ATTEMPTS = 10;
  errorTitle: string = "";
  errorText: string = "";
  
  constructor(
    private formBuilder: FormBuilder,
    readonly api: ApiService,
    readonly router: Router,
    @Inject(LOCALE_ID) readonly locale: string,
    private dialogService: DialogService,
    readonly aboutService: ApiAboutService
  ) {

    this.getTokenForm = this.formBuilder.group({
      emailAddress: ['', [Validators.required, Validators.email]],
    })
    this.codeForm = this.formBuilder.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
    this.voterID = history.state.voterID;
  }

  ngAfterViewInit() {
    if (document.readyState === "complete") {
      if(this.aboutService.isVotingCenter()) {
        this.resetFocus();
      } else {
        setTimeout(() => {
          //Fix for WAVE error *************************************************
          var response = document.getElementById("g-recaptcha-response");
          if (response) {
            response.setAttribute('aria-label', "Recaptcha response");
            response.setAttribute("aria-hidden", "true");
            response.setAttribute("aria-readonly", "true");
          }
          this.resetFocus();
        }, 200);
      }
    }
  }

  back() {
    this.router.navigate(passwordRoute, {state: {voterID: this.voterID, type: 'CHECK'}});
  }

  cancel() {
    this.router.navigate(homeRoute);
  }

  execute(){
    if(this.aboutService.isVotingCenter()) {
      this.getToken();
    } else {
      this.captchaComponent.execute();
    }
  }

  resolved(captchaResponse: string): void {
    this.recaptchaResponse = captchaResponse;
    Promise.resolve().then(() => {
      if(captchaResponse) {
        this.getToken();
      }
    }).catch(error => console.error("Error with captcha Response:", error));
  }

  getToken(){
    if(this.numResetAttempts >= this.MAX_RESET_ATTEMPTS) {
      this.errorText = $localize`Your account has been temporarily locked for failed verification code entry attempts. To unlock it, contact us!`;
      this.errorTitle = $localize`Maximum Verification Code Attempts.`;
      this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
      return;
    }
    this.displaySpinner = true;
    let request: any = {
      voter_id: this.voterID,
      emailAddress: this.getTokenForm.get('emailAddress')?.value,
      locale: this.locale
    }
    if(!this.aboutService.isVotingCenter()) {
      request["g-recaptcha-response"] = this.recaptchaResponse;
    }
    this.api.postWithHandshakeCheck(getResetPwTokenAPI, request).subscribe({
      next: () => {
        this.displaySpinner = false;
        this.codeSent = true;
        this.numResetAttempts = 0;
      },
      error: (error) => {
        this.displaySpinner = false;
        this.getTokenForm.reset();
        if(!this.aboutService.isVotingCenter()) {
          this.captchaComponent.resetCaptcha();
        }
        if(error.error.error_type === 'INVALID_IDENTITY') {
          this.numResetAttempts++;
          this.errorText = $localize`The data entered is incorrect. If you need help, please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Information not found`;
          this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
        } else if (error.error.error_type === 'INVALID_EMAIL_OR_TOKEN') {
          this.numResetAttempts++;
          this.errorText = $localize`The data entered is incorrect. If you need help, please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Information not found`;
          this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
        } else if (error.error.error_type === 'TOO_MANY_REQUESTS') {
          this.numResetAttempts = this.MAX_RESET_ATTEMPTS;
          this.errorText = $localize`Your account has been temporarily locked for failed verification code entry attempts. To unlock it, please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Maximum Verification Code Attempts.`;
          this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
        } else if (error.error.error_type === 'INVALID_API') {
          this.numResetAttempts++;
          this.errorText = $localize`The API key is invalid. Please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Invalid API Key`;
          this.dialogService.openDialog(DialogType.ALERT, this.errorTitle, this.errorText);
        } else if(error.error.error_type === 'ACCOUNT_LOCKED') {
          this.errorText = $localize`Your account has been locked. Please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Locked Account`
          this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
        } else if (error.error.message === 'Invalid recaptcha'){
          this.errorTitle = $localize`Captcha error.`;
          this.errorText = $localize`Please disable pop-up blockers or try using another browser or another device.`;
          this.dialogService.openDialog(DialogType.ALERT, this.errorTitle, this.errorText);
        } else if ((error.error_type !== undefined && error.error_type === 'ELECTION_CLOSED') ||
        (error.error !== undefined && error.error.error_type === 'ELECTION_CLOSED')) {
          this.errorText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
          this.errorTitle = $localize`Election Closed`
          this.dialogService.openDialog(DialogType.ALERT, this.errorTitle, this.errorText);
          setTimeout(() => {
            this.router.navigate(homeRoute);
          }, 5000);
        } else {
          this.errorText =  $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Error`
          this.dialogService.openDialog(DialogType.ALERT, this.errorTitle, this.errorText);
        } 
      }
    })
  }

  nextScreen() {
    if(this.stepper && this.stepper.selected) {
      this.stepper.selected.completed = true;
      this.stepper.next();
    }
  }

  isCodeValid(): boolean {
    return this.codeForm.valid;
  }

  getCode(form: FormGroup | undefined){;
    if(form === undefined){
      return undefined;
    }
    return form.get('code')?.value;
  }

  submit(){
    if(this.numResetAttempts >= this.MAX_RESET_ATTEMPTS) {
      this.errorText = $localize`Your account has been temporarily locked for failed verification code entry attempts. To unlock it, please use the "Contact Support" link for assistance.`;
      this.errorTitle = $localize`Maximum Verification Code Attempts.`;
      this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
      return;
    }
    this.displaySpinner = true;
    this.api.postWithHandshakeCheck(resetPasswordAPI, {
      voter_id: this.voterID,
      token: this.getCode(this.codeForm)
    }).subscribe({
      next: () => {
        this.displaySpinner = false;
        this.codeVerified = true;
        this.numResetAttempts = 0;
      },
      error: (error) => {
        this.displaySpinner = false;
        this.codeForm.reset();
        if(error.error.error_type === 'INVALID_IDENTITY') {
          this.numResetAttempts++;
          this.errorText = $localize`The data entered is incorrect. If you need help, please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Information not found`;
          this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
        } else if (error.error.error_type === 'INVALID_EMAIL_OR_TOKEN') {
          this.numResetAttempts++;
          this.errorText = $localize`The verification code entered is incorrect, you have `+(this.MAX_RESET_ATTEMPTS - this.numResetAttempts)+$localize` more attempts to enter it correctly.`;
          this.errorTitle = $localize`Invalid Code`;
          this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
        } else if (error.error.error_type === 'TOO_MANY_REQUESTS') {
          this.numResetAttempts = this.MAX_RESET_ATTEMPTS;
          this.errorText = $localize`Your account has been temporarily locked for failed verification code entry attempts. To unlock it, please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Maximum Verification Code Attempts.`;
          this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
        } else if (error.error.error_type === 'INVALID_API') {
          this.numResetAttempts++;
          this.errorText = $localize`The API key is invalid. Please consult the Help menu for assistance.`;
          this.errorTitle = $localize`Invalid API Key`;
          this.dialogService.openDialog(DialogType.ALERT, this.errorTitle, this.errorText);
        } else if ((error.error_type !== undefined && error.error_type === 'ELECTION_CLOSED') ||
        (error.error !== undefined && error.error.error_type === 'ELECTION_CLOSED')) {
          this.errorText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
          this.errorTitle = $localize`Election Closed`;
          this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
          setTimeout(() => {
            this.router.navigate(homeRoute);
          }, 5000);
        } else {
          this.errorText =  $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          this.errorTitle = $localize`Error`
          this.dialogService.openDialog(DialogType.ALERT, this.errorTitle, this.errorText);
        } 
      }
    })
  }

  toLogin() {
    this.router.navigate(homeRoute);
  }

  inputValidate(event: KeyboardEvent): void {
    const e = event;  
    const key = e.keyCode || e.which;  
    if (!(((key >= 48) && (key <= 57)) || (key == 8) || (key == 9) || (key == 127))) { //allow backspace //and delete
        if (e.preventDefault) e.preventDefault(); 
        if (e.returnValue) e.returnValue = false; 
    }
  }

  validateNumericInput(value: string): string {
    return /^\d$/.test(value) ? value : '';
  }

  handleInput(event: Event, index: number): void {
    const inputElement = event.target as HTMLInputElement;
    const value = this.validateNumericInput(inputElement.value.slice(0, 1));
    inputElement.value = value;

    if (!value) {
      this.updateFormControl(); // Actualiza el formulario inmediatamente si el valor no es válido
      return;
    }

    const nextInput = document.querySelectorAll<HTMLInputElement>('#reset-code > input')[index + 1];
    if (index === 5) {
      inputElement.select(); // Selecciona el valor en el último input para corrección
    } else if (nextInput) {
      nextInput.focus(); // Mueve el foco al siguiente input
    }

    this.updateFormControl();
  }

  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData?.getData('text') || '';
    const pastedValues = clipboardData.split('').filter((char) => /^\d$/.test(char)); // Solo números válidos

    if (pastedValues.length !== 6) {
      this.codeForm.get('code')?.setErrors({ invalidPaste: true });
      return;
    }

    const inputs = document.querySelectorAll<HTMLInputElement>('#reset-code > input');
    inputs.forEach((input, i) => {
      input.value = pastedValues[i] || ''; // Asigna los valores pegados
    });

    inputs[inputs.length - 1]?.focus(); // Foco al último input
    this.updateFormControl();
  }

  handleFocus(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.select(); // Selecciona el valor automáticamente
  }

  handleBlur(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.value = this.validateNumericInput(inputElement.value); // Valida el valor al perder el foco
    this.updateFormControl();
  }

  updateFormControl(): void {
    const inputs = document.querySelectorAll<HTMLInputElement>('#reset-code > input');
    const code = Array.from(inputs)
      .map((input) => input.value)
      .join('');

    const control = this.codeForm.get('code');
    control?.setValue(code);

    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      control?.setErrors({ invalidCode: true });
    } else {
      control?.setErrors(null);
    }
  }

  resetFocus() {
    let focusField = document.getElementById('accessibility-button')
    let focusFieldMobile = document.getElementById('accessibility-button-mobile')

    if (focusField) {
      focusField.focus()
    }

    if (focusFieldMobile) {
      focusFieldMobile.focus()
    }
  }
}
